/**
 * SnoozeDialog
 *
 * Modal for setting or removing a snooze on an incasso dossier.
 * Features preset date buttons, a custom date picker, and a required reason field.
 */

import { useState, useCallback, useEffect } from 'react';
import { addDays, addWeeks, addMonths, format, parseISO, isValid } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CalendarBlank } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from '@/lib/utils';

interface CurrentSnooze {
  until: string;
  reason: string;
}

interface SnoozeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (snoozeUntil: string, reason: string) => void;
  onUnsnooze?: () => void;
  ticketLabel?: string;
  currentSnooze?: CurrentSnooze | null;
}

type PresetKey = '3d' | '1w' | '2w' | '1m';

const PRESETS: { key: PresetKey; label: string; getDate: () => Date }[] = [
  { key: '3d', label: '3 dagen', getDate: () => addDays(new Date(), 3) },
  { key: '1w', label: '1 week', getDate: () => addWeeks(new Date(), 1) },
  { key: '2w', label: '2 weken', getDate: () => addWeeks(new Date(), 2) },
  { key: '1m', label: '1 maand', getDate: () => addMonths(new Date(), 1) },
];

const tomorrow = addDays(new Date(), 1);
const maxDate = addMonths(new Date(), 6);

export const SnoozeDialog = ({
  isOpen,
  onClose,
  onConfirm,
  onUnsnooze,
  ticketLabel,
  currentSnooze,
}: SnoozeDialogProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    currentSnooze ? parseISO(currentSnooze.until) : undefined,
  );
  const [reason, setReason] = useState<string>(currentSnooze?.reason ?? '');
  const [activePreset, setActivePreset] = useState<PresetKey | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Reset state when dialog opens or currentSnooze changes
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(currentSnooze ? parseISO(currentSnooze.until) : undefined);
      setReason(currentSnooze?.reason ?? '');
      setActivePreset(null);
      setCalendarOpen(false);
    }
  }, [isOpen, currentSnooze]);

  const handlePresetClick = useCallback((preset: (typeof PRESETS)[number]) => {
    setSelectedDate(preset.getDate());
    setActivePreset(preset.key);
    setCalendarOpen(false);
  }, []);

  const handleCalendarSelect = useCallback((date: Date | undefined) => {
    setSelectedDate(date);
    setActivePreset(null);
    setCalendarOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedDate || !reason.trim()) return;

    try {
      onConfirm(selectedDate.toISOString(), reason.trim());
    } catch (error) {
      console.error('[SnoozeDialog] Failed to confirm snooze:', error);
      throw error;
    }
  }, [selectedDate, reason, onConfirm]);

  const handleUnsnooze = useCallback(() => {
    try {
      onUnsnooze?.();
    } catch (error) {
      console.error('[SnoozeDialog] Failed to unsnooze:', error);
      throw error;
    }
  }, [onUnsnooze]);

  const isValid_ = selectedDate && isValid(selectedDate) && reason.trim().length > 0;

  const formattedPreview =
    selectedDate && isValid(selectedDate)
      ? format(selectedDate, 'd MMMM yyyy', { locale: nl })
      : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {currentSnooze ? 'Snooze aanpassen' : 'Dossier snoozen'}
          </DialogTitle>
          {ticketLabel && (
            <p className="text-sm text-neutral-secondary-mid">{ticketLabel}</p>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Preset buttons */}
          <div>
            <p className="text-xs font-medium text-neutral-secondary-mid mb-2 uppercase tracking-wide">
              Snooze voor
            </p>
            <div className="flex gap-2 flex-wrap">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.key}
                  type="button"
                  variant={activePreset === preset.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Custom date picker */}
          <div>
            <p className="text-xs font-medium text-neutral-secondary-mid mb-2 uppercase tracking-wide">
              Of kies een datum
            </p>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !selectedDate && 'text-neutral-secondary-mid',
                    activePreset === null && selectedDate && 'border-moss-primary',
                  )}
                >
                  <CalendarBlank weight="duotone" size={14} className="mr-2" />
                  {selectedDate && activePreset === null
                    ? format(selectedDate, 'd MMM yyyy', { locale: nl })
                    : 'Kies datum...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleCalendarSelect}
                  disabled={(date) => date < tomorrow || date > maxDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Preview */}
          {formattedPreview && (
            <p className="text-sm text-neutral-secondary-mid">
              Gesnoozed tot: <span className="font-medium text-neutral-900">{formattedPreview}</span>
            </p>
          )}

          {/* Info text */}
          <p className="text-xs text-neutral-secondary-mid leading-relaxed">
            Het dossier wordt verborgen tot de gekozen datum. Daarna verschijnt het automatisch weer in de lijst en het kanban.
          </p>

          {/* Reason */}
          <div>
            <p className="text-xs font-medium text-neutral-secondary-mid mb-2 uppercase tracking-wide">
              Reden <span className="text-red-500">*</span>
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Waarom wordt dit dossier gesnoozed?"
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {currentSnooze && onUnsnooze && (
            <Button
              type="button"
              variant="outline"
              onClick={handleUnsnooze}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 sm:mr-auto"
            >
              Snooze opheffen
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!isValid_}>
            Snooze instellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
